import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Images, Play, Youtube, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSite, UI, resolveImg } from "@/lib/site";
import { WaButton, InstaButton } from "@/components/WaButton";

export const ytId = (url) => {
  if (!url) return "";
  const m = String(url).match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
};

export const CaseModal = ({ item, onClose }) => {
  const { t } = useSite();
  const [i, setI] = useState(0);
  const vid = ytId(item?.youtube);
  const [video, setVideo] = useState(false);
  useEffect(() => { setI(0); setVideo(!!ytId(item?.youtube)); }, [item]);
  const imgs = Array.isArray(item?.images) ? item.images.filter(Boolean) : [];
  const n = imgs.length || 1;
  const go = (d) => setI((p) => (p + d + n) % n);

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-lenis-prevent className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-5xl overflow-y-auto border-[#e6ca7a]/20 bg-[#0e0c0a] p-0 text-[#f3e6c8] sm:rounded-2xl [&>button]:hidden" data-testid="case-modal">
        {item && (
          <>
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-black sm:aspect-[16/9]">
              {imgs.map((src, k) => (
                <motion.img
                  key={src + k}
                  src={resolveImg(src)}
                  alt={t(item.title)}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={false}
                  animate={{ opacity: k === i ? 1 : 0, scale: k === i ? 1 : 1.06 }}
                  transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0c0a] via-transparent to-transparent" />
              <button onClick={onClose} className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/50 backdrop-blur-md transition-colors hover:bg-[#e6ca7a] hover:text-black" data-testid="case-modal-close" aria-label="close"><X className="h-5 w-5" /></button>
              {video && vid && (
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1`}
                  title={t(item.title)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="case-modal-video"
                />
              )}
              {vid && (
                <button onClick={() => setVideo((v) => !v)} className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-xs font-semibold text-[#f0d78e] backdrop-blur-md transition-colors hover:bg-[#e6ca7a] hover:text-black" data-testid="case-modal-toggle-video">
                  {video ? <><Images className="h-4 w-4" />{t(UI.photos)}</> : <><Play className="h-4 w-4" />{t(UI.video)}</>}
                </button>
              )}
              {!video && n > 1 && (
                <div className="absolute bottom-4 right-4 flex items-center gap-2">
                  <span className="mr-2 text-xs tracking-widest text-[#e6ca7a]">{String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>
                  <button onClick={() => go(-1)} className="grid h-10 w-10 place-items-center rounded-full border border-[#e6ca7a]/40 bg-black/40 transition-colors hover:bg-[#e6ca7a] hover:text-black" data-testid="case-modal-prev" aria-label="prev"><ArrowLeft className="h-4 w-4" /></button>
                  <button onClick={() => go(1)} className="grid h-10 w-10 place-items-center rounded-full border border-[#e6ca7a]/40 bg-black/40 transition-colors hover:bg-[#e6ca7a] hover:text-black" data-testid="case-modal-next" aria-label="next"><ArrowRight className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="grid gap-6 p-6 sm:p-10 md:grid-cols-[1.4fr_1fr]">
              <div>
                <p className="eyebrow !text-[#e6ca7a]">{item.year}</p>
                <DialogTitle className="font-display mt-3 text-3xl font-medium leading-tight text-[#f6ecd9] sm:text-4xl">{t(item.title)}</DialogTitle>
                <DialogDescription className="mt-4 text-sm leading-relaxed text-[#cbbfa9] sm:text-base">{t(item.text)}</DialogDescription>
              </div>
              <div className="flex flex-col gap-3 md:items-end md:justify-end">
                {vid && !video && (
                  <button onClick={() => setVideo(true)} className="btn-ghost on-dark" data-testid="case-modal-youtube">
                    <Youtube className="h-4 w-4" /> {t(UI.watch)}
                  </button>
                )}
              <div className="flex flex-wrap gap-3 md:justify-end">
                <WaButton label={t(UI.wantSame)} text={`${t(UI.waMsg)} (${t(item.title)})`} testId="case-modal-whatsapp" />
                <InstaButton dark testId="case-modal-instagram" />
              </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
